import { getDeviceTimezone } from '@/utils/date';
import { HubCardType } from '@/types';
import { partnerSupplier } from '@/utils/environment';
import { ProfitRollupHubCardData } from '@/experiences/sales-portal/domain/insights/blocs/types/insight';
import { transformProfitRollup } from '@/experiences/sales-portal/domain/insights/blocs/transformers/profit-rollup-transformer';
import { useMemo } from 'react';
import { useSalesPortalGetHubCardDataQuery } from '@/experiences/sales-portal/domain/insights/blocs/documents/hub-card-data.generated';

export interface UseProfitRollupLoaderResult {
  data: ProfitRollupHubCardData | null;
  error: Error | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * Fetches Weekly Profit Rollup hub card data
 *
 * @param {string} userUUID - The authenticated user's UUID; the query is skipped while
 *   it is unavailable.
 * @param {boolean} isGrossProfitDisabled - boolean flag returned by backend used to hide gross profit data
 *
 * @returns {@link UseProfitRollupLoaderResult}
 *
 * @example
 * const { data, loading } = useProfitRollupLoader('123');
 */
export const useProfitRollupLoader = (
  userUUID: string,
  isGrossProfitDisabled: boolean,
): UseProfitRollupLoaderResult => {
  const { data, loading, error, refetch } = useSalesPortalGetHubCardDataQuery({
    fetchPolicy: 'cache-and-network',
    skip: !userUUID,
    variables: {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      cardType: HubCardType.WeeklyProfitRollup,
      userTimezone: getDeviceTimezone(),
      userUUID: userUUID,
    },
  });

  const transformedData = useMemo(
    () =>
      transformProfitRollup(
        data?.getHubCardData.card_data_json ?? [],
        isGrossProfitDisabled,
      ),
    [data, isGrossProfitDisabled],
  );

  return {
    data: transformedData,
    error: error ?? null,
    loading,
    refetch: () => {
      void refetch();
    },
  };
};
